/**
 * Centralized environment variable utilities
 * Single source of truth for all environment configuration
 */

/**
 * Get the API base URL from environment variables
 * Priority:
 * 1. window.RUNTIME_CONFIG.API_URL (runtime config from entrypoint script)
 * 2. VITE_API_URL (environment variable - build time)
 * 3. On-prem auto-detection (if VITE_DEPLOYMENT_TYPE=onprem or hostname suggests on-prem)
 * 4. Default production URL
 */
export function getApiBaseUrl(): string {
  // Check for runtime config first (set by entrypoint script)
  if (typeof window !== 'undefined' && (window as any).RUNTIME_CONFIG?.API_URL) {
    const runtimeUrl = (window as any).RUNTIME_CONFIG.API_URL;
    if (typeof window !== 'undefined' && import.meta.env.DEV) {
      console.log('[ENV DEBUG] Using runtime config API_URL:', runtimeUrl);
    }
    return runtimeUrl;
  }
  
  const envUrl = import.meta.env.VITE_API_URL;
  
  // If VITE_API_URL is set, use it
  if (envUrl) {
    return envUrl;
  }
  
  // Check for on-prem deployment type
  const deploymentType = import.meta.env.VITE_DEPLOYMENT_TYPE || 
    (typeof window !== 'undefined' && (window as any).RUNTIME_CONFIG?.DEPLOYMENT_TYPE);
  
  // On-prem auto-detection: check if deployment type is onprem or hostname suggests on-prem
  const isOnPrem = deploymentType === 'onprem' || 
    (typeof window !== 'undefined' && _isOnPremHostname(window.location.hostname));
  
  if (isOnPrem) {
    // For on-prem, construct API URL from current hostname
    if (typeof window !== 'undefined') {
      const protocol = window.location.protocol;
      const hostname = window.location.hostname;
      const port = window.location.port;
      // Use port 8000 if not specified (default API port)
      const apiPort = port || '8000';
      // If port is already in hostname or is standard HTTP/HTTPS port, don't add it
      const apiUrl = port && port !== '80' && port !== '443' 
        ? `${protocol}//${hostname}:${apiPort}`
        : `${protocol}//${hostname}:8000`;
      
      if (import.meta.env.DEV) {
        console.log('[ENV DEBUG] On-prem detected, using API URL:', apiUrl);
      }
      return apiUrl;
    }
    // Fallback for SSR or non-browser environments
    return 'http://localhost:8000';
  }
  
  // In development, default to localhost:8000 if no env var is set
  // This handles the case where frontend runs on localhost:3000 or any other port
  if (import.meta.env.DEV && !envUrl) {
    const devApiUrl = 'http://localhost:8000';
    if (typeof window !== 'undefined' && import.meta.env.DEV) {
      console.log('[ENV DEBUG] Development mode - using default API URL:', devApiUrl);
      console.log('[ENV DEBUG] Frontend running on:', window.location.origin);
      console.log('[ENV DEBUG] To override, set VITE_API_URL in .env.local');
      console.log('[ENV DEBUG] Deployment type:', deploymentType || 'auto-detected');
    }
    return devApiUrl;
  }
  
  // Default production URL
  return 'https://trace-api.alpyne.ai';
}

/**
 * Check if hostname suggests on-prem deployment
 * On-prem hostnames typically:
 * - Are localhost or local IPs (127.0.0.1, 192.168.x.x, 10.x.x.x, 172.16-31.x.x)
 * - Are internal domain names (e.g., *.local, *.internal, *.lan)
 * - Don't contain common cloud domains (alpyne.ai, azure, aws, etc.)
 */
function _isOnPremHostname(hostname: string): boolean {
  if (!hostname) return false;
  
  // Localhost - always treat as on-prem in development
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return true;
  }
  
  // Private IP ranges
  if (/^192\.168\./.test(hostname) || 
      /^10\./.test(hostname) || 
      /^172\.(1[6-9]|2[0-9]|3[0-1])\./.test(hostname)) {
    return true;
  }
  
  // Internal domain patterns
  if (/\.local$/.test(hostname) || 
      /\.internal$/.test(hostname) || 
      /\.lan$/.test(hostname) ||
      /\.corp$/.test(hostname)) {
    return true;
  }
  
  // Not a cloud domain (alpyne.ai, azure, aws, etc.)
  if (!/\.(alpyne\.ai|azure|aws|gcp|cloud)/.test(hostname)) {
    // Could be on-prem, but be conservative - only return true if explicitly marked
    return false;
  }
  
  return false;
}

/**
 * Get the API key from environment variables (optional)
 */
export function getApiKey(): string | undefined {
  return import.meta.env.VITE_API_KEY;
}

/**
 * Get the deployment environment
 */
export function getEnvironment(): 'development' | 'staging' | 'production' {
  return (import.meta.env.MODE as 'development' | 'staging' | 'production') || 'development';
}

/**
 * Get the base URL for routing (Vite's BASE_URL)
 * Priority:
 * 1. window.RUNTIME_CONFIG.BASE_URL (runtime config from entrypoint script)
 * 2. VITE_BASE_URL (environment variable - build time)
 * 3. import.meta.env.BASE_URL (Vite's default BASE_URL)
 * 4. Default to '/'
 */
export function getBaseUrl(): string {
  // Check for runtime config first (set by entrypoint script)
  if (typeof window !== 'undefined' && (window as any).RUNTIME_CONFIG?.BASE_URL) {
    const runtimeBaseUrl = (window as any).RUNTIME_CONFIG.BASE_URL;
    if (typeof window !== 'undefined' && import.meta.env.DEV) {
      console.log('[ENV DEBUG] Using runtime config BASE_URL:', runtimeBaseUrl);
    }
    return runtimeBaseUrl;
  }
  
  // Check for VITE_BASE_URL environment variable
  const envBaseUrl = import.meta.env.VITE_BASE_URL;
  if (envBaseUrl) {
    return envBaseUrl;
  }
  
  // Fall back to Vite's BASE_URL or default
  return import.meta.env.BASE_URL || '/';
}

/**
 * Get the SAM.gov API key from environment variables
 * 
 * IMPORTANT: Vite only exposes environment variables that start with VITE_ to the browser.
 * Use VITE_SAM_API_KEY in your .env file, not SAM_API_KEY.
 */
export function getSAMApiKey(): string | undefined {
  // Vite only exposes VITE_ prefixed vars to client
  const key = import.meta.env.VITE_SAM_API_KEY;
  if (import.meta.env.DEV) {
    if (!key) {
      console.warn('[getSAMApiKey] No SAM API key found. Set VITE_SAM_API_KEY in your .env file');
      console.warn('[getSAMApiKey] Note: Vite only exposes env vars starting with VITE_ to the browser');
    } else {
      console.log('[getSAMApiKey] SAM API key found, length:', key.length);
    }
  }
  return key;
}

